import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Job status actual-timestamp transitions (P3.4 / Logic #9).
 *
 * `actual_start` / `actual_end` were stamped with `now()` on EVERY PATCH whose
 * payload carried status='in_progress'/'completed' — so re-saving an already
 * in-progress job reset its real start time. They also keyed off the raw status
 * string, missing custom statuses (job_statuses is account-customisable; each
 * row maps to a stable `system_key`). This module decides the timestamp changes
 * from the system_key transition, and NEVER touches them on a same-status save.
 */
export type SystemKey = 'pending' | 'scheduled' | 'active' | 'completed' | 'cancelled' | null;

// Fallback when a status name isn't found in job_statuses (or the lookup
// fails). Covers the system defaults + the seeded custom names. Exported so
// the shared classification helpers in ./status.ts resolve identically.
export const RAW_KEY: Record<string, SystemKey> = {
  pending: 'pending',
  scheduled: 'scheduled',
  // 'travelling' is 'scheduled', NOT 'active': the engineer has set off but has
  // not arrived, so stamping actual_start here would record departure as
  // arrival and quietly wreck every on-time figure. It still classifies as
  // non-terminal, so the job keeps holding its diary slot while the van is out.
  travelling: 'scheduled',
  in_progress: 'active',
  active: 'active',
  to_invoice: 'completed',
  invoice_sent: 'completed',
  completed: 'completed',
  cancelled: 'cancelled',
  canceled: 'cancelled',
};

export function resolveSystemKey(map: Map<string, SystemKey>, status: string | null | undefined): SystemKey {
  if (!status) return null;
  if (map.has(status)) return map.get(status) ?? null;
  return RAW_KEY[status] ?? null;
}

/**
 * Pure transition table → which actual_* timestamps to set/clear. A same-key
 * move (re-save) returns {} so existing timestamps are never clobbered.
 */
export function timestampUpdatesForTransition(opts: {
  fromKey: SystemKey;
  toKey: SystemKey;
  hasActualStart: boolean;
  hasActualEnd: boolean;
  now: string;
}): { actual_start?: string | null; actual_end?: string | null } {
  const out: { actual_start?: string | null; actual_end?: string | null } = {};
  if (opts.fromKey === opts.toKey) return out; // re-PATCH same status → never clobber

  if (opts.toKey === 'active') {
    if (!opts.hasActualStart) out.actual_start = opts.now;
    if (opts.hasActualEnd) out.actual_end = null; // reopened — no longer finished
  } else if (opts.toKey === 'completed') {
    /**
     * 🚨 `actual_start` is NOT back-filled, and it used to be.
     *
     * The old line was `if (!hasActualStart) out.actual_start = now` — so a job
     * taken straight to done without anyone marking arrival recorded a start
     * time equal to its end time. A visit of zero minutes, written into the one
     * column the On Time Tracker measures from.
     *
     * That is not a rounding error, it is a fabricated fact wearing the shape of
     * a measured one (anti-pattern #19). On 5 Sept 2026 production held 191
     * finished jobs recording UNDER TEN SECONDS on site, across 35 of 38
     * accounts, against 48 jobs in the whole 1–15 minute band — a cluster at
     * zero with an empty valley after it, which is what tapping "Arrived" and
     * "Done" in the same second looks like in the data.
     *
     * If nobody said they arrived, we do not know when they did. NULL says
     * exactly that, and every reader already handles a job with no start time.
     * `actual_end` is still stamped, because somebody IS telling us, right now,
     * that the work is finished — that one is a measurement.
     */
    if (!opts.hasActualEnd) out.actual_end = opts.now;
  } else if (opts.toKey === 'scheduled' || opts.toKey === 'pending') {
    if (opts.hasActualStart) out.actual_start = null; // un-started
    if (opts.hasActualEnd) out.actual_end = null;
  }
  // 'cancelled': leave actuals as a historical record of what happened.
  return out;
}

async function loadStatusKeyMap(supabase: SupabaseClient, accountId: string): Promise<Map<string, SystemKey>> {
  const map = new Map<string, SystemKey>();
  const { data } = await supabase
    .from('job_statuses')
    .select('name, system_key')
    .eq('account_id', accountId);
  for (const row of data || []) map.set(row.name as string, (row.system_key as SystemKey) ?? null);
  return map;
}

/**
 * Resolve the actual_start/actual_end changes for a job status change. Returns
 * {} when status isn't changing (so callers can spread it harmlessly). Looks up
 * the account's job_statuses to get system_keys, falling back to known names.
 */
export async function computeStatusTimestamps(
  supabase: SupabaseClient,
  accountId: string,
  params: {
    fromStatus: string | null | undefined;
    toStatus: string;
    hasActualStart: boolean;
    hasActualEnd: boolean;
    now: string;
  },
): Promise<{ actual_start?: string | null; actual_end?: string | null }> {
  const map = await loadStatusKeyMap(supabase, accountId);
  return timestampUpdatesForTransition({
    fromKey: resolveSystemKey(map, params.fromStatus),
    toKey: resolveSystemKey(map, params.toStatus),
    hasActualStart: params.hasActualStart,
    hasActualEnd: params.hasActualEnd,
    now: params.now,
  });
}
